import Cookies from 'js-cookie'

// Types
export type TokenType = 'Access-Token' | 'Refresh-Token' | 'User-Chat-Token'
export type JWTToken = string
export type RefreshToken = string

interface TokenConfig {
  readonly ACCESS_TOKEN: string
  readonly COOKIE_OPTIONS: {
    expires: number
    path: string
  }
  readonly REFRESH_TOKEN: string
  readonly USER_CHAT_TOKEN: string
}

// Configuration
const TOKEN_CONFIG: TokenConfig = {
  ACCESS_TOKEN: 'chaine-access-token',
  COOKIE_OPTIONS: {
    expires: 60 * 60 * 24 * 90,
    path: '/' // 90 days
  },
  REFRESH_TOKEN: 'chaine-refresh-token',
  USER_CHAT_TOKEN: 'chaine-user-chat-token'
} as const

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined'

export const tokenUtils = {
  /**
   * Clears all cookies from local storage
   */
  clearLocalStore: (): void => {
    if (!isBrowser) return

    // Remove cookies with the same options they were set with
    Object.keys(Cookies.get()).forEach((cookieName) => {
      Cookies.remove(cookieName, { path: '/' })
    })

    // Clear localStorage
    Object.keys(localStorage).forEach((localStorageKey) => {
      localStorage.removeItem(localStorageKey)
    })
  },

  /**
   * Gets cookie data with type safety
   */
  getCookie: <T>(key: string): T | null => {
    if (!isBrowser) return null
    const data = Cookies.get(key)
    return data ? JSON.parse(data) : null
  },

  /**
   * Gets data from local storage
   */
  getDataFromLocalStore: (key: string): string | null => {
    if (!isBrowser) return null
    return localStorage.getItem(key)
  },

  /**
   * Gets a token by type
   */
  getToken: (tokenType: TokenType): JWTToken | RefreshToken | null => {
    if (!isBrowser) return null
    const tokenName = tokenUtils.getTokenName(tokenType)
    return Cookies.get(tokenName) || null
  },

  /**
   * Gets the token name based on token type
   */
  getTokenName: (tokenType: TokenType): string => {
    const tokenMap = {
      'Access-Token': TOKEN_CONFIG.ACCESS_TOKEN,
      'Refresh-Token': TOKEN_CONFIG.REFRESH_TOKEN,
      'User-Chat-Token': TOKEN_CONFIG.USER_CHAT_TOKEN
    }
    return tokenMap[tokenType] || ''
  },

  /**
   * Gets user ID from access token
   */
  getUserID: (): string | null => {
    if (!isBrowser) return null
    try {
      const token = tokenUtils.getToken('Access-Token')
      if (!token) return null

      const parsedToken = tokenUtils.parseJwt(token)
      return parsedToken?.username || null
    } catch {
      return null
    }
  },

  /**
   * Checks if user is authenticated by verifying access token existence
   */
  isAuthenticated: (): boolean => {
    if (!isBrowser) return false
    return tokenUtils.getToken('Access-Token') !== null
  },

  /**
   * Parses a JWT token and returns the payload
   */
  parseJwt: (token: string): { username: string } | null => {
    if (!isBrowser) return null
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )

      return JSON.parse(jsonPayload)
    } catch {
      return null
    }
  },

  /**
   * Removes a token
   */
  removeToken: (tokenType: TokenType): void => {
    if (!isBrowser) return
    const tokenName = tokenUtils.getTokenName(tokenType)

    // Try removing with domain first (for production)
    Cookies.remove(tokenName, { path: '/' })
  },

  /**
   * Sets cookie data with type safety
   */
  setCookie: <T>(key: string, data: T): void => {
    if (!isBrowser) return
    Cookies.set(key, JSON.stringify(data), TOKEN_CONFIG.COOKIE_OPTIONS)
  },

  /**
   * Sets a token
   */
  setToken: (tokenType: TokenType, token: JWTToken | RefreshToken): void => {
    if (!isBrowser) return
    const tokenName = tokenUtils.getTokenName(tokenType)
    Cookies.set(tokenName, token, TOKEN_CONFIG.COOKIE_OPTIONS)
  }
}
